/**
 * What a recommendation card says about what the plant did with it, what the effect check says of a change it tried,
 * and what a person records (L1 and L2 of the 2026-10-01 learning-loop plan). Plain words; pure.
 */
import type {
  RecommendationBaseline,
  RecommendationEffect,
  RecommendationEvent,
  RecommendationEventCreate,
  RecommendationEventKind,
  RecommendationScope,
  RecommendationVerdict,
} from "@/lib/api/recommendations";
import { dayNumber, formatDay, formatInstantDay, isPlantDay } from "@/lib/plant-days";

/** Who recorded an event, in words. */
function whoOf(e: RecommendationEvent): string {
  return e.actor_name.trim() || "someone";
}

/** Names once each, trimmed, whatever their case, the single one first (the Go mirror is recommendations.Scope.PartNames). */
function namesOf(one: string | undefined, many: readonly string[] | undefined): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const v of [one ?? "", ...(many ?? [])]) {
    const t = v.trim();
    if (t && !seen.has(t.toLowerCase())) {
      seen.add(t.toLowerCase());
      out.push(t);
    }
  }
  return out;
}

/** "a", "a and b", "a, b and c". */
function andList(names: readonly string[]): string {
  return names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** Where a change applied, when narrower than the plant: " on Line 2, for part P-100, on shift 1". */
export function scopeWords(scope: RecommendationScope | null | undefined): string {
  if (!scope) return "";
  const out: string[] = [];
  const lines = namesOf(scope.line, scope.lines);
  const parts = namesOf(scope.part, scope.parts);
  if (lines.length) out.push(`on ${andList(lines)}`);
  if (parts.length) out.push(`${parts.length === 1 ? "for part" : "for parts"} ${andList(parts)}`);
  if (scope.shift?.trim()) out.push(`on shift ${scope.shift.trim()}`);
  return out.length ? ` ${out.join(", ")}` : "";
}

/** Each verdict in words: always since the change, never because of it. */
const VERDICT_WORDS: Record<RecommendationVerdict, string> = {
  improved: "improved since the change",
  worse: "worse since the change",
  no_clear_change: "no clear change since the change",
  too_early: "too early to tell",
  cant_tell: "can't tell",
};

/** A verdict in words; null for one this page does not know. */
export function verdictWords(verdict: unknown): string | null {
  return typeof verdict === "string" && Object.prototype.hasOwnProperty.call(VERDICT_WORDS, verdict)
    ? VERDICT_WORDS[verdict as RecommendationVerdict]
    : null;
}

/** The verdicts that read the data: "so far" while the after window is not complete. */
const READ_VERDICTS: ReadonlySet<string> = new Set(["improved", "worse", "no_clear_change"]);

/**
 * What the effect check says of a tried change, in one phrase: "improved since the change (Sand inclusion: 7.3% of
 * castings before, 2.6% after; 28 days since the change)", "improved since the change, so far (...; 14 of 28 days
 * since the change)" while its after window is not complete, "too early to tell (4 of 10 days since the change so
 * far)", "can't tell: the recommendation names no defect". Null before the check has read it, or for a verdict this
 * page does not know.
 */
export function effectWords(effect: RecommendationEffect | null | undefined): string | null {
  const verdict = verdictWords(effect?.verdict);
  if (!effect || !verdict) return null;
  const words = READ_VERDICTS.has(effect.verdict) && effect.result?.complete === false ? `${verdict}, so far` : verdict;
  const detail = typeof effect.result?.detail === "string" ? effect.result.detail.trim() : "";
  if (!detail) return words;
  return effect.verdict === "cant_tell" ? `${words}: ${detail}` : `${words} (${detail})`;
}

/** A parameter's window in words: "35 to 45", "at least 35", "at most 45". */
function windowWords(w: { lo?: number; hi?: number } | undefined): string {
  const lo = typeof w?.lo === "number" ? w.lo : null;
  const hi = typeof w?.hi === "number" ? w.hi : null;
  if (lo !== null && hi !== null) return `${lo} to ${hi}`;
  if (lo !== null) return `at least ${lo}`;
  if (hi !== null) return `at most ${hi}`;
  return "no end set";
}

/** A kept or reverted state's tried change and what it did: ". Tried on 3 Aug 2026: improved since the change (...)". */
function triedSince(e: RecommendationEvent): string {
  const eff = effectWords(e.tried?.effect);
  if (!e.tried || !eff || !isPlantDay(e.tried.plant_day)) return "";
  return `. Tried on ${formatDay(e.tried.plant_day)}: ${eff}`;
}

/**
 * A recommendation's state, in one line: "Tried on 3 Aug 2026 by Asha: improved since the change (...)", "Marked wrong
 * by Asha on 5 Aug 2026: the probe was out". A tried, kept or reverted change is dated by the plant day it applied
 * from, and says what the effect check found since; a verdict is dated by the day it was recorded.
 */
export function stateLine(e: RecommendationEvent): string {
  const who = whoOf(e);
  const day = e.plant_day && isPlantDay(e.plant_day) ? formatDay(e.plant_day) : formatInstantDay(e.created_at);
  const recorded = formatInstantDay(e.created_at);
  const note = e.note.trim();
  const why = note ? `: ${note}` : "";
  switch (e.kind) {
    case "tried": {
      const base = `Tried on ${day}${scopeWords(e.scope)} by ${who}`;
      const eff = effectWords(e.effect);
      if (!eff) return `${base}${why}`;
      return `${base}: ${eff}${note ? `. Note: ${note}` : ""}`;
    }
    case "kept":
      return `Kept on ${day} by ${who}${why}${triedSince(e)}`;
    case "reverted":
      return `Undone on ${day} by ${who}${why}${triedSince(e)}`;
    case "wrong":
      return `Marked wrong by ${who} on ${recorded}${why}`;
    case "not_applicable":
      return `Marked as not applying here by ${who} on ${recorded}${why}`;
    case "reopened":
      return `Reopened by ${who} on ${recorded}${why}`;
  }
}

/**
 * What the effect check read beyond its line, one note each: the stretch it was compared with when the plant pinned
 * one, whether the parameter the recommendation names held its window (each stretch against its own window when the
 * plant changed it, and when a day counts as inside), and what else moved in the window (named, never weighed). Empty
 * before the check has read it.
 */
export function effectNotes(effect: RecommendationEffect | null | undefined): string[] {
  const r = effect?.result;
  if (!r) return [];
  const out: string[] = [];
  const g = r.comparison;
  if (g && Array.isArray(g.untreated) && g.untreated.length > 0) {
    out.push(`Compared with the parts that did not get the change: ${andList(g.untreated)}.`);
  }
  const b = r.before;
  if (b?.kind === "pinned" && isPlantDay(b.from) && isPlantDay(b.to)) {
    out.push(`Compared with the stretch pinned as stable, ${formatDay(b.from)} to ${formatDay(b.to)}.`);
  }
  const l = r.lever;
  if (l) {
    const label = l.label || l.param;
    if (l.before && l.after && l.before.days > 0 && l.after.days > 0) {
      const windows = l.window && l.window_after
        ? ` (${windowWords(l.window_after)} since the change, ${windowWords(l.window)} before)`
        : "";
      const at = typeof l.inside_at === "number" && l.inside_at > 0 && l.inside_at <= 1
        ? ` (a day inside when at least ${Math.round(l.inside_at * 100)}% of its readings were)`
        : "";
      out.push(
        `${label} was inside its window${windows} on ${l.after.inside} of ${l.after.days} days since the change, against ` +
          `${l.before.inside} of ${l.before.days} before${at}${l.held === false ? ": the change did not hold" : ""}.`,
      );
    } else if (l.why) {
      out.push(`${label}: ${l.why}.`);
    }
  }
  for (const c of r.confounds ?? []) {
    if (typeof c?.words === "string" && c.words.trim()) out.push(c.words.trim());
  }
  return out;
}

/**
 * The kinds of what else moved in a tried change's window, in a few words each (the effect check's confound kinds,
 * platform-core/internal/twin/recommendation_effect.go; the weekly digest says them in the same words). "reverted" is
 * left out: a card's state line says the change was undone.
 */
const CONFOUND_WORDS: Record<string, string> = {
  overlap: "another change tried",
  mix: "the mix of parts moved",
  parts_left_out: "parts made in one window alone",
  baseline_drift: "the rate already moving before the change",
  data_gap: "days the roll-ups do not hold",
  late_data: "data late on days read",
  calendar: "the plant's clock or calendar changed",
  vocabulary: "a defect word bound",
  scope_not_separated: "a scope the roll-ups cannot separate",
  no_comparison: "no part left to compare it with",
  rescoped: "marked tried again with other parts or an earlier day",
};

/**
 * What else moved in a tried change's window, and whether the setting it names held its window, in one line beside
 * the verdict on a card (the #1139 review, MEDIUM 1): "Also in its window: another change tried; the mix of parts
 * moved; the setting did not hold its window." Each kind once, named and never weighed; a kind this page does not know
 * is left out. Null when the check named nothing.
 */
export function effectCaveats(effect: RecommendationEffect | null | undefined): string | null {
  const r = effect?.result;
  if (!r) return null;
  const out: string[] = [];
  for (const c of r.confounds ?? []) {
    const words = typeof c?.kind === "string" && Object.prototype.hasOwnProperty.call(CONFOUND_WORDS, c.kind) ? CONFOUND_WORDS[c.kind] : null;
    if (words && !out.includes(words)) out.push(words);
  }
  if (r.lever?.held === true) out.push("the setting held its window");
  if (r.lever?.held === false) out.push("the setting did not hold its window");
  return out.length ? `Also in its window: ${out.join("; ")}.` : null;
}

/** A recommendation someone marked wrong, or as not applying: its card offers to reopen it. */
export function isClosed(e: RecommendationEvent | undefined): boolean {
  return e?.kind === "wrong" || e?.kind === "not_applicable";
}

/**
 * What the "Mark as tried" form holds. The stable stretch (baselineFrom, baselineTo) is optional: a stretch of plant
 * days before the change the plant knows was stable, compared with in place of the days just before it.
 */
export interface TriedFields {
  plantDay: string;
  line: string;
  part: string;
  shift: string;
  note: string;
  baselineFrom?: string;
  baselineTo?: string;
}

/** What the "Doesn't apply / is wrong" form holds: which of the two, and why. */
export interface VerdictFields {
  kind: Extract<RecommendationEventKind, "wrong" | "not_applicable">;
  reason: string;
}

/** A request ready to send, or what stops it, in plain words. */
export type RecordRequest = { body: RecommendationEventCreate; problem?: undefined } | { body?: undefined; problem: string };

/** The longest note, text shown or scope the platform keeps, and the longest stable stretch. */
const NOTE_MAX = 2000;
const SCOPE_MAX = 200;
/** The parts or the lines a scope lists at most (recommendations.ScopeListMax). */
const SCOPE_LIST_MAX = 20;
const BASELINE_MAX_DAYS = 400;
/** The longest text of a recommendation the platform keeps (characters, not UTF-16 units: the server counts runes). */
export const TEXT_MAX = 2000;

/** The recommendation as shown, cut to what the platform keeps: a long sentence still records. */
export function clampText(text: string): string {
  const chars = Array.from(text.trim());
  return chars.length <= TEXT_MAX ? chars.join("") : chars.slice(0, TEXT_MAX).join("");
}

/** Marking a recommendation tried: from a plant day (today on the plant's clock unless changed), maybe narrower. */
export function triedRequest(key: string, text: string, f: TriedFields): RecordRequest {
  if (!isPlantDay(f.plantDay)) return { problem: "Choose the day the change started." };
  const scope: RecommendationScope = {};
  // A line or a part may be several, separated by commas: one stays as before, more go as a list (B3 T3).
  for (const [name, value] of [["line", f.line], ["part", f.part]] as const) {
    const names = namesOf("", value.split(","));
    if (names.some((v) => v.length > SCOPE_MAX)) return { problem: `Keep each ${name} under ${SCOPE_MAX} characters.` };
    if (names.length > SCOPE_LIST_MAX) return { problem: `Name at most ${SCOPE_LIST_MAX} ${name}s.` };
    if (names.length === 1) scope[name] = names[0];
    else if (names.length > 1) scope[name === "line" ? "lines" : "parts"] = names;
  }
  const shift = f.shift.trim();
  if (shift.length > SCOPE_MAX) return { problem: `Keep the shift under ${SCOPE_MAX} characters.` };
  if (shift) scope.shift = shift;
  const note = f.note.trim();
  if (note.length > NOTE_MAX) return { problem: `Keep the note under ${NOTE_MAX} characters.` };
  let baseline: RecommendationBaseline | undefined;
  const from = (f.baselineFrom ?? "").trim();
  const to = (f.baselineTo ?? "").trim();
  if (from || to) {
    if (!isPlantDay(from) || !isPlantDay(to)) return { problem: "Choose both days of the stable stretch, or neither." };
    if (to < from) return { problem: "The stable stretch ends before it starts." };
    if (to >= f.plantDay) return { problem: "The stable stretch ends before the day the change started." };
    if (dayNumber(to) - dayNumber(from) + 1 > BASELINE_MAX_DAYS) {
      return { problem: `Keep the stable stretch to ${BASELINE_MAX_DAYS} days at most.` };
    }
    baseline = { from, to };
  }
  return {
    body: {
      key,
      text_shown: clampText(text),
      kind: "tried",
      plant_day: f.plantDay,
      ...(Object.keys(scope).length ? { scope } : {}),
      ...(baseline ? { baseline } : {}),
      ...(note ? { note } : {}),
      via: "web",
    },
  };
}

/** Saying a recommendation is wrong, or does not apply: the reason is required. */
export function verdictRequest(key: string, text: string, f: VerdictFields): RecordRequest {
  const reason = f.reason.trim();
  if (!reason) return { problem: "Say why, so the next brief and the consultant can take it into account." };
  if (reason.length > NOTE_MAX) return { problem: `Keep the reason under ${NOTE_MAX} characters.` };
  return { body: { key, text_shown: clampText(text), kind: f.kind, note: reason, via: "web" } };
}

/** Reopening a recommendation marked wrong or not applying. */
export function reopenRequest(key: string, text: string): RecommendationEventCreate {
  return { key, text_shown: clampText(text), kind: "reopened", via: "web" };
}

/** A change that was tried is kept, from today on the plant's clock (when it is a day). */
export function keptRequest(key: string, text: string, today: string): RecommendationEventCreate {
  return { key, text_shown: clampText(text), kind: "kept", ...(isPlantDay(today) ? { plant_day: today } : {}), via: "web" };
}

/** A change that was tried is undone ("Reverted"), from today on the plant's clock (when it is a day). */
export function revertedRequest(key: string, text: string, today: string): RecommendationEventCreate {
  return { key, text_shown: clampText(text), kind: "reverted", ...(isPlantDay(today) ? { plant_day: today } : {}), via: "web" };
}
